/**
 * AdMob in the Android / iOS apps (Capacitor): the banner at the top of the Lobby, the
 * waiting room and the game page, the rewarded video that unlocks a table colour
 * (showRewardedVideo), and the end-of-game ad after the tap that leaves a game (showEndOfGameAd).
 * Nothing happens on the web: the plugin is loaded only inside the native app.
 *
 * The ids are not secret (they ship inside every app). Google's test banner is
 * shown only in development (`yarn start`) or when REACT_APP_ADS_TEST=true at
 * build time; every build (preview included) shows the real ads. Tapping your
 * own real ads gets the AdMob account banned: declare your phone in AdMob →
 * Paramètres → Appareils de test, it then gets the real ads in test mode.
 */
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { ADS } from "./features";

// Banner ad units ("Bannière") of the Rami Online apps in AdMob: ca-app-pub-XXXXXXXX/YYYYYYYY.
// The app ids (ca-app-pub-XXXXXXXX~ZZZZZZZZ) go in AndroidManifest.xml and Info.plist.
const BANNER_IDS = {
  android: "ca-app-pub-8890772866910960/1119901660",
  ios: "ca-app-pub-8890772866910960/2530940276",
};

// Google's public test ids: https://developers.google.com/admob/android/test-ads
const TEST_BANNER_IDS = {
  android: "ca-app-pub-3940256099942544/6300978111",
  ios: "ca-app-pub-3940256099942544/2934735716",
};

const PLATFORM = Capacitor.getPlatform(); // "android" | "ios" | "web"
const NATIVE = Capacitor.isNativePlatform() && !!TEST_BANNER_IDS[PLATFORM];
const ENABLED = ADS && NATIVE;
const TEST = process.env.NODE_ENV !== "production" || process.env.REACT_APP_ADS_TEST === "true";
const REAL_ID = TEST ? null : BANNER_IDS[PLATFORM] || null;

// A plain 320 × 50 banner (BANNER): its height is known before the ad comes,
// so the page keeps that room from the start and the board does not jump down
// when the ad shows. CSS px = dp in the web view.
const BANNER_HEIGHT = 50;

// Promise of the plugin's MODULE, once AdMob is initialised (and consent asked).
// Never resolve a promise with the `AdMob` plugin itself: a Capacitor plugin is a
// proxy that turns every property into a native call, `then` included, so the
// promise takes it for a thenable, calls `AdMob.then(...)`, and never settles.
// That kept the banner from ever showing (Android builds 4 and 5).
let ready = null;

/**
 * Starts AdMob once: the consent form for players in Europe / the UK (Google's
 * UMP form, set up in AdMob → "Confidentialité et messages"), then the iOS
 * tracking prompt. A refusal still shows ads, just not personalised ones.
 */
function start() {
  if (!ready) {
    ready = (async () => {
      const mod = await import("@capacitor-community/admob");
      const { AdMob, AdmobConsentStatus } = mod;
      await AdMob.initialize({ initializeForTesting: !REAL_ID });
      try {
        const info = await AdMob.requestConsentInfo();
        if (info.isConsentFormAvailable && info.status === AdmobConsentStatus.REQUIRED) {
          await AdMob.showConsentForm();
        }
      } catch (e) {
        console.warn("[ads] consent", e);
      }
      if (PLATFORM === "ios") {
        try {
          const { status } = await AdMob.trackingAuthorizationStatus();
          if (status === "notDetermined") await AdMob.requestTrackingAuthorization();
        } catch (e) {
          console.warn("[ads] tracking", e);
        }
      }
      return mod;
    })().catch((e) => {
      console.warn("[ads] init", e);
      ready = null;
      return null;
    });
  }
  return ready;
}

// One native banner, shared by the pages that want it (Lobby, Room, Game). Going
// from one to the other (Lobby → waiting room → game, a rematch's new Game) keeps the
// ad on screen: no new request, and AdMob's own refresh goes on. The page that
// leaves must not take the banner away from the one that comes: its
// removeBanner() could reach the plugin after the new page's showBanner(). So the
// banner stays while a page uses it, and goes a moment after the last one leaves.
const users = new Set(); // setHeight of each mounted page
const watchers = new Set(); // useAdTop(): the room at the top, for the toasts
let status = "off"; // "off" | "loading" | "loaded" | "failed"
let adHeight = BANNER_HEIGHT;
let generation = 0; // bumped by every show and hide: a late step of an older one stops
let listening = null;
let hideTimer = null;

// The banner refreshes by itself every 30 s: that is set on the ad unit in AdMob
// (Paramètres avancés → Actualisation automatique; Google allows 30 to 150 s),
// never with a timer here. The SDK then stops refreshing while nobody sees the ad.
//
// No ad (no fill, no network, or a refresh that failed: the plugin then destroys
// the banner, and its refresh with it). The room stays, shown as an empty band of
// the table's felt (Game.jsx, owner's request), and the banner is asked again at
// the same rhythm, 30 s later — never while the app is in the background, where
// nobody would see it. Nothing jumps: the room is already there.
const RETRY_MS = 30_000;
let retryTimer = null;

// The room the page leaves at its top: kept from the start, whether an ad comes or not.
function pageHeight() {
  if (status === "loaded") return adHeight;
  if (status === "loading" || status === "failed") return BANNER_HEIGHT;
  return 0;
}

function setStatus(s) {
  status = s;
  if (s === "failed") scheduleRetry();
  const h = pageHeight();
  users.forEach((fn) => fn(h));
  watchers.forEach((fn) => fn(h));
  // The native banner covers the top of the web view: centred dialogs move down by
  // half of it (components/ui/dialog.jsx).
  document.documentElement.style.setProperty("--ad-top", `${h}px`);
}

function scheduleRetry() {
  clearTimeout(retryTimer);
  retryTimer = setTimeout(retryWhenSeen, RETRY_MS);
}

function retryWhenSeen() {
  if (!users.size || status !== "failed") return;
  if (document.visibilityState === "hidden") {
    document.addEventListener("visibilitychange", retryWhenSeen, { once: true });
    return;
  }
  showTopBanner();
}

async function showTopBanner() {
  if (status === "loading" || status === "loaded") return;
  const gen = ++generation;
  setStatus("loading");
  try {
    const mod = await start();
    if (gen !== generation) return;
    if (!mod) {
      setStatus("failed");
      return;
    }
    const { AdMob, BannerAdPluginEvents, BannerAdPosition, BannerAdSize } = mod;
    if (!listening) {
      listening = Promise.all([
        // The plugin also sends a height of 0 when the banner goes away: the
        // failure has its own event, the removal is ours.
        AdMob.addListener(BannerAdPluginEvents.SizeChanged, (s) => {
          if (s?.height > 0 && (status === "loading" || status === "loaded")) {
            adHeight = s.height;
            setStatus("loaded");
          }
        }),
        AdMob.addListener(BannerAdPluginEvents.FailedToLoad, (e) => {
          console.warn("[ads] banner", e);
          if (status !== "off") setStatus("failed");
        }),
      ]).catch((e) => {
        listening = null;
        throw e;
      });
    }
    await listening;
    if (gen !== generation) return;
    await AdMob.showBanner({
      adId: REAL_ID || TEST_BANNER_IDS[PLATFORM],
      isTesting: !REAL_ID,
      adSize: BannerAdSize.BANNER,
      position: BannerAdPosition.TOP_CENTER,
      margin: 0,
      npa: true, // non-personalised ads only
    });
  } catch (e) {
    console.warn("[ads] show", e);
    if (gen === generation) setStatus("failed");
  }
}

function hideTopBanner() {
  clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    if (users.size || status === "off") return;
    generation++;
    clearTimeout(retryTimer);
    setStatus("off");
    start().then((mod) => mod?.AdMob.removeBanner().catch(() => {}));
  }, 400);
}

/**
 * Shows the banner at the top of the screen while the calling page is mounted,
 * and removes it when no page wants it any more. Returns the height in CSS px
 * the page leaves at its top, since the native banner is drawn over the web
 * view: 50 from the start and while no ad comes (the page shows an empty band
 * there), 0 outside the apps.
 */
export function useTopBanner() {
  const [h, setH] = useState(() => (!ENABLED ? 0 : status === "loaded" ? adHeight : BANNER_HEIGHT));
  useEffect(() => {
    if (!ENABLED) return undefined;
    users.add(setH);
    clearTimeout(hideTimer);
    showTopBanner();
    setH(pageHeight());
    return () => {
      users.delete(setH);
      hideTopBanner();
    };
  }, []);
  return h;
}

/**
 * The room the banner takes at the top of the screen, without asking for one:
 * for what sits at the top of every page (the toasts, App.js). 0 outside the apps
 * and on the pages without the banner.
 */
export function useAdTop() {
  const [h, setH] = useState(() => (ENABLED ? pageHeight() : 0));
  useEffect(() => {
    if (!ENABLED) return undefined;
    watchers.add(setH);
    setH(pageHeight());
    return () => {
      watchers.delete(setH);
    };
  }, []);
  return h;
}

// ── Rewarded video: unlocks a table colour (lib/tableUnlocks.js) ────────────

// "Avec récompense" ad units of the Rami Online apps in AdMob ("Couleur de
// table"). Test builds and `yarn start` use Google's test video instead.
const REWARD_IDS = {
  android: "ca-app-pub-8890772866910960/8323905580",
  ios: "ca-app-pub-8890772866910960/4847870015",
};

// Google's public test ids: https://developers.google.com/admob/android/test-ads
const TEST_REWARD_IDS = {
  android: "ca-app-pub-3940256099942544/5224354917",
  ios: "ca-app-pub-3940256099942544/1712485313",
};

const REWARD_ID = !NATIVE ? null : TEST ? TEST_REWARD_IDS[PLATFORM] : REWARD_IDS[PLATFORM] || null;
// On the website in development only (`yarn start`): a pretend video, so the
// unlock can be tried without the app. The real website has no video: its
// players unlock the colours in the app (owner's choice).
const FAKE_REWARD = !NATIVE && process.env.NODE_ENV === "development";

// Whether this page can play a rewarded video.
export const rewardedVideoAvailable = () => !!REWARD_ID || FAKE_REWARD;
// Whether this is the Android / iOS app (the website has no video at all).
export const isNativeApp = () => NATIVE;

// A video loaded ahead, so the tap plays it at once. AdMob keeps a loaded
// rewarded ad for an hour; past that it is loaded again.
const REWARD_MAX_AGE = 50 * 60 * 1000;
let rewardLoad = null; // { promise of the plugin's module, at }

/** Loads a video ahead (the colour picker calls it when it opens). */
export function preloadRewardedVideo() {
  if (!REWARD_ID) return Promise.resolve(null);
  if (rewardLoad && Date.now() - rewardLoad.at < REWARD_MAX_AGE) return rewardLoad.promise;
  const promise = (async () => {
    const mod = await start();
    if (!mod) throw new Error("AdMob unavailable");
    await mod.AdMob.prepareRewardVideoAd({ adId: REWARD_ID, isTesting: TEST, npa: true });
    return mod; // the module, never the plugin (see `ready`)
  })();
  const entry = { promise, at: Date.now() };
  rewardLoad = entry;
  promise.catch((e) => {
    console.warn("[ads] reward load", e);
    if (rewardLoad === entry) rewardLoad = null;
  });
  return promise;
}

// Last full-screen ad seen (a colour video or the end-of-game ad): no other one for AD_GAP_MS.
let lastFullScreenAt = 0;

/**
 * Plays a rewarded video. `onStart` is called just before it shows (only when
 * one does). Resolves (never rejects) to:
 * - "rewarded": watched to the end;
 * - "dismissed": closed before the end;
 * - "failed": no video came (no fill, no network) or it could not play;
 * - "unavailable": no video here (the website, or no ad unit yet).
 *
 * The plugin's showRewardVideoAd() settles only when the reward is earned: a
 * video closed early would leave it pending for ever. The close event ends it.
 */
export async function showRewardedVideo({ onStart } = {}) {
  if (FAKE_REWARD) {
    onStart?.();
    await new Promise((r) => setTimeout(r, 1500));
    return "rewarded";
  }
  if (!REWARD_ID) return "unavailable";
  let mod;
  try {
    mod = await preloadRewardedVideo();
  } catch {
    return "failed";
  }
  rewardLoad = null; // a loaded video plays once
  const { AdMob, RewardAdPluginEvents } = mod;
  return new Promise((resolve) => {
    let rewarded = false;
    let done = false;
    const handles = [];
    const finish = (result) => {
      if (done) return;
      done = true;
      if (result !== "failed") lastFullScreenAt = Date.now();
      handles.forEach((h) => h.then((x) => x.remove()).catch(() => {}));
      resolve(result);
    };
    handles.push(
      AdMob.addListener(RewardAdPluginEvents.Rewarded, () => { rewarded = true; }),
      // Some networks send the reward a moment after the close: wait for it.
      AdMob.addListener(RewardAdPluginEvents.Dismissed, () => {
        setTimeout(() => finish(rewarded ? "rewarded" : "dismissed"), 500);
      }),
      AdMob.addListener(RewardAdPluginEvents.FailedToShow, (e) => {
        console.warn("[ads] reward show", e);
        finish("failed");
      }),
    );
    Promise.all(handles)
      .then(() => {
        onStart?.();
        return AdMob.showRewardVideoAd();
      })
      .then(() => { rewarded = true; })
      .catch((e) => {
        console.warn("[ads] reward show", e);
        finish("failed");
      });
  });
}

// ── End-of-game ad (interstitial): when a player leaves the end of their game ──
//
// Only after a tap, never by itself when the game ends (owner's rule; AdMob also asks
// that an interstitial follow an action of the player, and the winner keeps the
// trophy screen): « أرجع للقهوة » at the end of the game, « طرح آخر » when the player
// was the only human (nobody waits in the next waiting room), or the exit button of a
// player eliminated before the end. Game.jsx decides which taps count; the tap's own
// action goes on when the ad is closed, or at once when there is none.

// "Interstitiel" ad units of the Rami Online apps in AdMob ("Fin de partie"). Development
// and REACT_APP_ADS_TEST builds use Google's test ad.
const INTERSTITIAL_IDS = {
  android: "ca-app-pub-8890772866910960/1127263225",
  ios: "ca-app-pub-8890772866910960/6595212322",
};
const TEST_INTERSTITIAL_IDS = {
  android: "ca-app-pub-3940256099942544/1033173712",
  ios: "ca-app-pub-3940256099942544/4411468910",
};
const INTERSTITIAL_ID = !ENABLED ? null : TEST ? TEST_INTERSTITIAL_IDS[PLATFORM] : INTERSTITIAL_IDS[PLATFORM] || null;

const AD_GAP_MS = 3 * 60_000; // at most one full-screen ad (this one or a colour video) in 3 minutes
const MIN_GAME_MS = 2 * 60_000; // none after a game shorter than this
const LOAD_WAIT_MS = 2500; // at the tap, an ad still loading gets this long, then the tap goes on
const SHOW_WAIT_MS = 5000; // an ad that does not open within this is dropped
const CLOSE_WAIT_MS = 120_000; // nor may a close that never comes hold the button for ever
const gamesWithAd = new Set(); // one per game, even when no ad came
let interstitialLoad = null; // { promise of the plugin's module, at }

/** Whether leaving this game would show the ad (`playedMs`: how long the game lasted). */
export function endOfGameAdDue(gameId, playedMs) {
  return (
    !!INTERSTITIAL_ID &&
    !!gameId &&
    !gamesWithAd.has(gameId) &&
    playedMs >= MIN_GAME_MS &&
    Date.now() - lastFullScreenAt >= AD_GAP_MS
  );
}

/** Loads the ad ahead, when the game ends for the player (Game.jsx). */
export function preloadEndOfGameAd() {
  if (!INTERSTITIAL_ID) return Promise.resolve(null);
  if (interstitialLoad && Date.now() - interstitialLoad.at < REWARD_MAX_AGE) return interstitialLoad.promise;
  const promise = (async () => {
    const mod = await start();
    if (!mod) throw new Error("AdMob unavailable");
    await mod.AdMob.prepareInterstitial({ adId: INTERSTITIAL_ID, isTesting: TEST, npa: true });
    return mod; // the module, never the plugin (see `ready`)
  })();
  const entry = { promise, at: Date.now() };
  interstitialLoad = entry;
  promise.catch((e) => {
    console.warn("[ads] interstitial load", e);
    if (interstitialLoad === entry) interstitialLoad = null;
  });
  return promise;
}

/**
 * The tap that leaves the game: shows the ad if one is due, and resolves (never
 * rejects) once it is closed — "shown" — or at once: "skipped" (none due: not in
 * the app, no ad unit yet, already one for this game, one seen less than 3 minutes
 * ago, a short game) or "failed" (none loaded in time, or it could not open).
 * The plugin's showInterstitial() settles when the ad opens, not when it closes:
 * the close event ends the wait.
 */
export async function showEndOfGameAd(gameId, playedMs) {
  if (!endOfGameAdDue(gameId, playedMs)) return "skipped";
  gamesWithAd.add(gameId);
  let mod;
  try {
    mod = await Promise.race([
      preloadEndOfGameAd(),
      new Promise((_, reject) => setTimeout(() => reject(new Error("too slow")), LOAD_WAIT_MS)),
    ]);
  } catch {
    return "failed"; // a load that finishes later stays ready for the next game
  }
  if (!mod) return "failed";
  interstitialLoad = null; // a loaded ad plays once
  const { AdMob, InterstitialAdPluginEvents } = mod;
  return new Promise((resolve) => {
    let done = false;
    let guard = null;
    const handles = [];
    const finish = (result) => {
      if (done) return;
      done = true;
      clearTimeout(guard);
      handles.forEach((h) => h.then((x) => x.remove()).catch(() => {}));
      resolve(result);
    };
    guard = setTimeout(() => finish("failed"), SHOW_WAIT_MS);
    handles.push(
      AdMob.addListener(InterstitialAdPluginEvents.Showed, () => {
        lastFullScreenAt = Date.now();
        clearTimeout(guard);
        guard = setTimeout(() => finish("shown"), CLOSE_WAIT_MS);
      }),
      AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => finish("shown")),
      AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, (e) => {
        console.warn("[ads] interstitial show", e);
        finish("failed");
      }),
    );
    Promise.all(handles)
      .then(() => AdMob.showInterstitial())
      .catch((e) => {
        console.warn("[ads] interstitial show", e);
        finish("failed");
      });
  });
}
